import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useApiClient } from "@/src/hooks/useApiClient";
import { useAppToast } from "@/src/hooks/useAppToast";
import { useTranslations } from "@/src/i18n/i18n";
import { invalidateApiPaths } from "@/src/libs/invalidateApiPaths";

/**
 * Les chemins qu'une action de modération rend faux.
 *
 * ⚠️ **La liste, la fiche, ses commentaires ET le tableau de bord.** Le chemin
 * est comparé **en entier** par React Query, ce n'est pas un préfixe de chaîne :
 * les nommer un par un est la seule façon de tous les atteindre
 * (cf. `invalidateApiPaths`). Omettre la fiche laissait la publication « En
 * attente » juste après l'avoir validée ; omettre le tableau de bord y laissait
 * un compteur qui ment jusqu'au prochain rechargement.
 */
const CHEMINS_TOUCHES = [
  "/api/v1/moderation/post",
  "/api/v1/moderation/post/{id}",
  "/api/v1/moderation/post/{id}/comment",
  "/api/v1/moderation/post/{id}/like",
];

/**
 * Valider, refuser, effacer une publication — et effacer un commentaire.
 *
 * ## Valider et refuser notifient l'auteur — côté serveur
 *
 * Rien à faire ici : le serveur envoie la notification dans les deux sens. Une
 * approbation est une bonne nouvelle qu'il attend ; un refus est une décision
 * qu'il doit connaître, sans quoi son post reste invisible sans explication.
 *
 * ⚠️ **Seuls `approved` et `rejected` sont acceptés.** `scanning` et
 * `pending_review` sont des états que la machine attribue ; renvoyer une
 * publication vers `scanning` ne relancerait aucun classement — aucune reprise
 * n'est planifiée — et elle y resterait bloquée. Le serveur refuse en 400, et
 * l'écran ne propose donc pas le geste.
 *
 * ## La suppression de commentaire vit ici aussi, désormais
 *
 * Elle était réservée à `useSanction` tant que les signalements étaient le seul
 * écran où l'on voyait un commentaire. La fiche d'une publication en affiche le
 * fil entier : lui refuser le geste obligerait à passer par un signalement pour
 * retirer un commentaire qu'on a sous les yeux. Les deux appelants partagent la
 * même route et la même confirmation nommée — c'est `ModerationAction` qui tient
 * cette règle, pas chaque écran.
 */
export function usePostActions() {
  const $api = useApiClient();
  const t = useTranslations("posts");
  const queryClient = useQueryClient();
  const { showSuccess, showApiError } = useAppToast();

  /**
   * L'identifiant de la publication en cours de traitement, s'il y en a une.
   *
   * Les commentaires ont le leur : sur la fiche, les deux gestes coexistent, et
   * un seul état ferait tourner le bouton de la publication pendant qu'on
   * supprime un commentaire.
   */
  const [pendingId, setPendingId] = useState<number>();
  const [pendingCommentId, setPendingCommentId] = useState<number>();

  const moderate = $api.useMutation("patch", "/api/v1/moderation/post/{id}");
  const remove = $api.useMutation("delete", "/api/v1/moderation/post/{id}");
  const removeComment = $api.useMutation(
    "delete",
    "/api/v1/moderation/post/comment/{commentId}",
  );

  /*
    Les callbacks sont passés **à l'appel**, pas au hook : c'est ici qu'on sait
    quel message afficher, information que la mutation ignore. Ce sont bien les
    `onSuccess` / `onError` de React Query, pas un effet sur `isSuccess`.
  */
  const handlers = (successKey: "approved" | "rejected" | "deleted") => ({
    onSuccess: async () => {
      await invalidateApiPaths(queryClient, CHEMINS_TOUCHES);
      showSuccess(t(`toast-${successKey}`));
    },
    onError: (error: unknown) => showApiError(t("action-error"), error),
    onSettled: () => setPendingId(undefined),
  });

  const approve = (id: number) => {
    setPendingId(id);
    moderate.mutate(
      { params: { path: { id } }, body: { status: "approved" } },
      handlers("approved"),
    );
  };

  const reject = (id: number) => {
    setPendingId(id);
    moderate.mutate(
      { params: { path: { id } }, body: { status: "rejected" } },
      handlers("rejected"),
    );
  };

  /**
   * Suppression **définitive** — cascades sur photos, cœurs et commentaires,
   * plus l'effacement des objets S3. À ne pas confondre avec la suppression par
   * un auteur, qui est douce et laisse la ligne lisible pour la modération.
   */
  const destroy = (id: number, options?: { onSuccess?: () => void }) => {
    setPendingId(id);
    remove.mutate(
      { params: { path: { id } } },
      {
        ...handlers("deleted"),
        onSuccess: async () => {
          await invalidateApiPaths(queryClient, CHEMINS_TOUCHES);
          showSuccess(t("toast-deleted"));
          options?.onSuccess?.();
        },
      },
    );
  };

  /**
   * Efface définitivement un commentaire, et ses réponses par cascade.
   *
   * Pas de suppression douce ici : c'est la sanction, pas le geste d'un auteur
   * qui se ravise.
   */
  const destroyComment = (commentId: number) => {
    setPendingCommentId(commentId);
    removeComment.mutate(
      { params: { path: { commentId } } },
      {
        onSuccess: async () => {
          await invalidateApiPaths(queryClient, CHEMINS_TOUCHES);
          showSuccess(t("toast-comment-deleted"));
        },
        onError: (error: unknown) => showApiError(t("action-error"), error),
        onSettled: () => setPendingCommentId(undefined),
      },
    );
  };

  return {
    approve,
    reject,
    destroy,
    destroyComment,
    pendingId,
    pendingCommentId,
  };
}
